import { createFileRoute } from '@tanstack/react-router'
import { CURRENT_SCHEMA_VERSION } from '@/shared/projects/migrations'
import { getProject } from '@/infrastructure/storage'
import { hydrateDraft } from '@/smm/hydrate'
import { getDraftSummary } from '@/smm/api'
import { createLogger } from '@/shared/logging/logger'

const log = createLogger('editor-route-loader')

/** Resolve the freecut project for the editor, hydrating from the SMM API if
 *  the user deep-linked to a draft that hasn't been hydrated into OPFS yet
 *  (the dashboard's Drafts table links straight here without going through
 *  the freecut homepage's Open button, which is where hydrate normally runs). */
export async function loadEditorProject(projectId: string) {
  let project = await getProject(projectId)

  if (!project) {
    // Best-effort: try to recover the project version so autosave keeps
    // conflict detection intact. A failure here is fine — hydrate without
    // a version still produces a working editor.
    let version: number | undefined
    try {
      const summary = await getDraftSummary(projectId)
      version = summary.projectVersion
    } catch (e) {
      log.warn('draft summary lookup failed; hydrating without version', e)
    }

    try {
      await hydrateDraft(projectId, version)
    } catch (e) {
      log.warn('hydrate-on-miss failed', e)
      throw new Error(`Project not found: ${projectId}`)
    }
    project = await getProject(projectId)
  }

  if (!project) {
    throw new Error(`Project not found: ${projectId}`)
  }

  const storedSchemaVersion = project.schemaVersion ?? 1
  return {
    project: {
      id: project.id,
      name: project.name,
      width: project.metadata.width,
      height: project.metadata.height,
      fps: project.metadata.fps,
      backgroundColor: project.metadata.backgroundColor,
    },
    migration: {
      storedSchemaVersion,
      currentSchemaVersion: CURRENT_SCHEMA_VERSION,
      requiresUpgrade: storedSchemaVersion < CURRENT_SCHEMA_VERSION,
    },
  }
}

export const Route = createFileRoute('/editor/$projectId')({
  // Editor loader data is tiny and migration state must be fresh on reopen.
  // Avoid keeping inactive editor matches around with stale "requires upgrade" flags.
  gcTime: 0,
  preloadGcTime: 0,
  loader: ({ params }) => loadEditorProject(params.projectId),
})
